/* =========================================================================
 * CORRECT THIS FIELD, ON A PURSUIT RECORD — the pure model. PURE, ZERO IMPORTS.
 *
 * Brian: "these all must have read, and write functionality."
 *
 * The Command Center has been able to FIND a bad pursuit record since the
 * category rail shipped — the Gaps view, the honest empty states, the
 * "Unassigned" owner column all work — and then the fix lived nowhere. Corporate
 * is 1,783 records whose two most valuable fields (owner and pursuit level) are
 * recorded as unset on most of them. That backfill is what this exists to
 * enable.
 *
 * ⚠️ THIS FILE CONTAINS NO FIELD NAMES, NO LABELS AND NO TYPES. THAT IS THE
 * WHOLE POINT, AND IT IS THE ONE THING NOT TO "HELPFULLY" ADD LATER.
 *
 * `fieldEdit.js` — the championship editor beside this one — carries
 * `FIELD_SPECS`, a hardcoded label/type list. It survives there only because
 * `specDrift()` reconciles it against the server every load and reports
 * divergence, and it survives at all because `GET /api/champion-fields` serves
 * no `specs` key to replace it with (measured 2026-09-14: its top-level keys are
 * exactly `ok · object · tiers · never · lock · caller`).
 *
 * `GET /api/pursuit-fields` DOES serve `specs` — label, type, and the real enum
 * options read from the portal's live schema — and that route was built
 * carrying it for exactly this reason. So this module reads every label, every
 * type and every enum option off the policy at runtime. There is no second
 * hardcoded list, and a field renamed, retyped or re-optioned server-side
 * arrives here correct with no edit.
 *
 * ⚠️ PURE, WITH ZERO IMPORTS, AND THAT IS LOAD-BEARING. The network half is
 * `pursuitEditRun.js`. `lib/api.js` reads `import.meta.env`, so anything
 * importing it cannot be driven under plain Node — and every rule in this file
 * is a rule `verify-spaces.mjs` has to EXECUTE rather than regex for. Same
 * split, and the same reason, as `clearCrown.js` beside `clearCrownRun.js`.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()
const arr = (v) => (Array.isArray(v) ? v : [])

/* ---- THE REASON FLOOR ---------------------------------------------------
 *
 * ⚠️ AND ON THIS ROUTE IT IS **CLIENT-SIDE ONLY**, WHICH IS MEASURED, NOT
 * ASSUMED — and it was measured the expensive way.
 *
 * `tools/pursuit-write-probe.mjs` step 5 POSTed an operational field with NO
 * reason, expecting the refusal shape. The route answered
 * `200 { ok:true, wrote:['city'] }` (ledger seq 29818) and a live corporate
 * record's city became "Nowhere". It was restored by
 * `tools/pursuit-revert-city.mjs` (seq 29819) and the revert confirmed by
 * re-read, and that probe step is deleted.
 *
 * So: a PRIVILEGED write genuinely requires a reason server-side (the
 * `data_repair_locked` refusal names it). An OPERATIONAL one does not. This
 * floor is therefore a UX guarantee, not enforcement — hard rule 5, stated
 * rather than implied, so nobody later reads "the reason is required" as
 * meaning the ledger can be trusted to always carry one.
 */
export const REASON_MIN = 12

/* ---- READING THE POLICY ------------------------------------------------
 *
 * `GET /api/pursuit-fields?line=<line>`. Measured live 2026-09-14 across all
 * eight HubSpot lines; the shapes below are what the route actually returns,
 * not what the plan document proposed. Two of them differ from the proposal and
 * both differences would have produced a broken control:
 *
 *   spec.type is "enumeration", NOT "enum"   — a client filtering on `enum`
 *                                              finds zero and renders every
 *                                              enum as a free-text box, which
 *                                              is the exact `sr_segment` trap.
 *   unavailable is [{ field, why }], NOT     — a client treating it as strings
 *   a list of strings                          greys out nothing.
 */
export function readPursuitPolicy(res) {
  const r = res || {}
  const tiers = r.tiers || {}
  const caller = r.caller || {}
  const list = (v) => arr(v).map(S).filter(Boolean)

  /* `unavailable` is a list of objects — field + why it is absent. Kept as a
   * map so the reason can be shown on the disabled control rather than a bare
   * greyed box nobody can explain. */
  const unavailable = {}
  for (const u of arr(r.unavailable)) {
    if (typeof u === 'string') unavailable[u] = 'not_on_portal'
    else if (u && S(u.field)) unavailable[S(u.field)] = S(u.why) || 'unavailable'
  }

  return {
    ok: r.ok === true,
    line: S(r.line),
    label: S(r.label),
    /* ⚠️ THE CLIENT MUST NOT INFER THIS. Corporate/Greek/Cheer/… are
     * `companies`; RFP is `deals`, with an entirely different field set. */
    objectType: S(r.objectType),
    operational: list(tiers.operational?.fields),
    privileged: list(tiers.privileged?.fields),
    operationalRequires: S(tiers.operational?.requires),
    privilegedRequires: S(tiers.privileged?.requires),
    never: list(r.never),
    unavailable,
    specs: r.specs && typeof r.specs === 'object' ? r.specs : {},
    /* ⚠️ `mayWrite` IS THE AUTHORITATIVE PER-CALLER ANSWER, read in preference
     * to re-deriving it from (tier + canRepairData). The tier lists say what a
     * field IS; only this says what THIS session may do. Re-deriving would be a
     * second copy of the server's rule, and the two would disagree silently the
     * day the server grew a third tier or a per-line grant. */
    mayWrite: list(caller.mayWrite),
    userId: S(caller.userId),
    role: S(caller.role),
    canRepairData: caller.canRepairData === true,
    /* ⚠️ READ, NEVER ASSUMED. It is one env var from changing, and on the RFP
     * (deals) line the property it names is ALSO in `unavailable` — the line
     * declares an owner property the portal does not hold. That has to render
     * as the honest "no writable owner on this line yet", not as a blank box. */
    ownerProperty: S(r.owner?.property),
  }
}

/* ---- THE SPEC — labels, types and enum options, all the server's ---------
 *
 * HubSpot's own vocabulary, normalized to what an input needs. The mapping is
 * the only place a type word appears in this file, and it maps FROM the
 * server's words rather than declaring a set of our own.
 */
export function specOf(policy, field) {
  const raw = policy?.specs?.[S(field)]
  if (!raw || typeof raw !== 'object') return null

  const type = S(raw.type).toLowerCase()
  const fieldType = S(raw.fieldType).toLowerCase()

  /* HubSpot `type` → the control to draw. `enumeration` is HubSpot's word for
   * an enum and it is the one that matters: an enum typed free-hand is how
   * `sr_segment` gets accepted by a route and rejected by HubSpot — 68 PSAL
   * records were lost exactly that way. */
  let kind = 'text'
  if (type === 'enumeration') kind = (fieldType === 'booleancheckbox') ? 'boolean' : 'enum'
  else if (type === 'bool' || fieldType === 'booleancheckbox') kind = 'boolean'
  else if (type === 'number') kind = 'number'
  else if (type === 'date' || type === 'datetime') kind = 'date'

  const options = arr(raw.options)
    .map((o) => (typeof o === 'string'
      ? { value: o, label: o }
      : { value: S(o?.value), label: S(o?.label) || S(o?.value) }))
    .filter((o) => o.value !== '')

  /* An enum the portal declares with no options cannot be safely offered as a
   * select, and offering it as free text is the trap. It is drawn disabled. */
  const emptyEnum = kind === 'enum' && options.length === 0

  return {
    field: S(field),
    label: S(raw.label) || S(field),
    kind,
    type,
    fieldType,
    options,
    emptyEnum,
    /* HubSpot's own read-only / calculated flags. A calculated property accepts
     * a write and recomputes over it, which is a mismatch waiting to happen. */
    readOnly: raw.readOnly === true,
    calculated: raw.calculated === true,
  }
}

/** The tier a field sits in. `never` and `unknown` are told apart: one is a
 *  server decision, the other means we are asking about a field the server has
 *  not heard of — usually a rename, and never something to draw an input for. */
export function tierOfPursuitField(policy, field) {
  const f = S(field)
  const p = policy || {}
  if ((p.never || []).includes(f)) return 'never'
  if ((p.operational || []).includes(f)) return 'operational'
  if ((p.privileged || []).includes(f)) return 'privileged'
  return 'unknown'
}

/* ---- MAY THIS CALLER EDIT THIS FIELD, AND IF NOT, WHY -------------------
 *
 * Three outcomes, not two, and the third is the one that matters:
 *
 *   drawable: false  — there is no question to answer. A `never` field and an
 *                      unrecognised field get NO control at all, not a disabled
 *                      one. `dealstage` and `pipeline` are on the RFP line's
 *                      never-list precisely because writing them would make
 *                      this route the path by which the app advances a
 *                      human-owned deal — a separate decision with its own
 *                      consequence (PURSUIT-WRITE-PLAN §5). Offering either,
 *                      even greyed out, invites somebody to ask for it.
 *   editable: false  — the field is real and correctable, this session just may
 *                      not — or the portal does not hold the property at all.
 *                      Shown, disabled, WITH the reason. A rep who sees no
 *                      control concludes the feature is broken and asks for it
 *                      to be fixed.
 *   editable: true   — draw the input.
 *
 * ⚠️ THIS IS A DRAWING DECISION ONLY (hard rule 5). `POST /api/pursuit-update`
 * re-decides every clause against the verified JWT and answers 403
 * `data_repair_locked` / `field_forbidden` — both measured live. Nothing here
 * secures anything, and the server's refusal is what gets displayed when the
 * two disagree.
 */
export function mayEditPursuitField(policy, field) {
  const p = policy || {}
  const f = S(field)
  const tier = tierOfPursuitField(p, f)

  /* ⚠️ A POLICY THAT FAILED TO LOAD REMOVES THE CONTROLS. It does not draw
   * live-looking inputs that 403 on every press. */
  if (!p.ok) {
    return {
      drawable: false, editable: false, tier,
      why: 'The field policy has not loaded, so nothing here knows what may be edited.',
    }
  }

  if (tier === 'never') {
    return {
      drawable: false, editable: false, tier,
      why: `The server lists ${f} as never writable through this route.`,
    }
  }

  /* ⚠️ DECLARED BY POLICY, ABSENT FROM THE PORTAL. The route would accept it
   * and HubSpot would drop it — which is the 200-on-a-write-that-never-landed
   * failure this whole build is shaped around. Greyed out, with the reason.
   *
   * ⚠️ AND THIS IS CHECKED **BEFORE** THE TIER, WHICH IS NOT COSMETIC ORDERING.
   * An unavailable field is typically absent from every tier too — the server
   * has nothing to tier a property the portal does not hold — so `sr_pursuit_status`
   * resolves to tier `unknown`. Checked after the tier, it would fall into the
   * "renamed server-side" branch and be silently NOT DRAWN, which is precisely
   * the outcome the `unavailable` list exists to prevent: a rep looking for the
   * pursuit-status field would find no control and no explanation, and conclude
   * the editor was broken rather than that the property needs creating. */
  const gap = p.unavailable?.[f]
  if (gap) {
    return {
      drawable: true, editable: false, tier, unavailable: true,
      why: gap === 'not_on_portal'
        ? `${f} is in this line's policy but the property does not exist on the portal, so a write `
          + `would be accepted here and silently dropped by HubSpot. It has to be created first.`
        : `${f} is unavailable on this line (${gap}).`,
    }
  }

  if (tier === 'unknown') {
    return {
      drawable: false, editable: false, tier,
      why: `${f} is not in any tier the field policy reports. It may have been renamed server-side.`,
    }
  }

  const spec = specOf(p, f)
  if (spec?.readOnly || spec?.calculated) {
    return {
      drawable: true, editable: false, tier,
      why: `HubSpot reports ${spec.label} as ${spec.calculated ? 'calculated' : 'read-only'} — it is `
        + `derived by the portal, so a value written here would be recomputed over.`,
    }
  }
  if (spec?.emptyEnum) {
    return {
      drawable: true, editable: false, tier,
      why: `${spec.label} is an enum and the portal reported no options for it. Offering a free-text `
        + `box would produce a value HubSpot rejects after the route has accepted the call.`,
    }
  }

  if (!(p.mayWrite || []).includes(f)) {
    return {
      drawable: true, editable: false, tier,
      why: tier === 'privileged'
        ? `Correcting ${f} requires ${p.privilegedRequires || 'exec or the Data Repair grant'}. `
          + `This session is ${p.role || 'unrecognised'}${p.canRepairData ? '' : ' without that grant'}.`
        : `This session may not write ${f}.`,
    }
  }

  return { drawable: true, editable: true, tier, why: '' }
}

/* ---- WHAT TO DRAW, AND IN WHAT ORDER -----------------------------------
 *
 * ⚠️ THE ORDER IS THE SERVER'S TOO. `fieldEdit.js` has a `SURFACED` array
 * because it also has `FIELD_SPECS`; there is no such array here. The policy's
 * own tier lists are the order — operational first (the pursuit itself: who
 * owns it, how warm it is, the facts that size it), privileged after (the
 * record's identity, which moves it out of the space entirely).
 *
 * A field with no spec is NOT drawn: without a label and a type it would be an
 * unlabelled free-text box over a live write route. The probe reports that case
 * loudly (`tier field with NO spec`) so it is a backend bug to fix, not a
 * silent omission — and measured today it is empty on all eight lines.
 */
export function pursuitEditableFields(policy, record) {
  const p = policy || {}
  if (!p.ok) return []
  const order = [...(p.operational || []), ...(p.privileged || [])]
  const seen = new Set()

  return order
    .map((field) => {
      if (seen.has(field)) return null
      seen.add(field)
      const spec = specOf(p, field)
      if (!spec) return null
      const gate = mayEditPursuitField(p, field)
      if (!gate.drawable) return null
      return {
        field,
        spec,
        ...gate,
        current: currentPursuitValue(record, field),
        derived: pursuitDerivedNote(field),
        isOwner: field === p.ownerProperty && !!p.ownerProperty,
      }
    })
    .filter(Boolean)
}

/** Fields the policy declares but serves no spec for — a backend gap that would
 *  otherwise show up as a field quietly missing from the editor. The mirror of
 *  `specDrift()` on the championship side, pointed the other way: there the
 *  client's list could name a field the server dropped; here the server's list
 *  can name a field it forgot to describe. */
export function pursuitSpecGaps(policy) {
  const p = policy || {}
  if (!p.ok) return []
  return [...(p.operational || []), ...(p.privileged || [])].filter((f) => !specOf(p, f))
}

/* ---- THE DERIVED FIELDS — READ OFF `pursuitAdapter`, NOT OFF A GUESS -----
 *
 * ⚠️ THIS IS THE `sr_state` "Georgia" vs "GA" TRAP ON A DIFFERENT OBJECT, AND
 * IT IS LIVE ON CORPORATE RIGHT NOW. Record 55774529893 (Charter
 * Communications) stores `state: "Connecticut"`; `adaptPursuitRow` runs it
 * through `normalizeState` and the board shows **CT**. Show the operator CT as
 * the current value of a property holding "Connecticut" and two things break at
 * once: a real edit to "CT" is refused as a no-change, and a good write is
 * reported as a mismatch by the re-read, which compares the raw property.
 *
 * ⚠️ AND `isDerivedField` FROM `fieldEdit.js` DOES NOT PORT. It names
 * `sr_state · sr_conference · sr_date_status · sr_event_date` because
 * `normalizeChampionship` transforms those four. `pursuitAdapter` has its OWN,
 * completely different set. Reusing the championship list would have marked
 * `sr_event_date` (which no pursuit line even has) and missed every one below.
 *
 * So the set is derived from what the ADAPTER actually does to each raw
 * property, named transform by named transform — and `verify-spaces.mjs` proves
 * every entry by EXECUTING the real `adaptPursuitRow` against a raw value and
 * asserting the normalized output differs. A declaration nobody executes is a
 * second copy of the adapter waiting to drift; one the suite drives is a check.
 */
export const ADAPTER_DERIVED = {
  /* `normalizeState({ sr_state: row.state })` → `record.state` — "Connecticut"
   * becomes "CT", and blank/USA/Canada become "NATIONAL". */
  state: { via: 'normalizeState', shows: 'state' },
  sr_state: { via: 'normalizeState', shows: 'state' },

  /* `num()` — strips non-numerics and returns null for blank, so the stored
   * string "94500" is displayed as the number 94500 and "94,500" would be too. */
  numberofemployees: { via: 'num()', shows: 'employees' },
  amount: { via: 'num()', shows: 'value' },

  /* `safeUrl()` — anything that is not a real http(s) address is displayed as
   * blank, so the board can read empty while the property holds a value. */
  website: { via: 'safeUrl()', shows: 'website' },
  sr_rfp_source_url: { via: 'safeUrl() + the URL_FIELDS priority walk', shows: 'sourceUrl' },
  sr_rfp_portal_url: { via: 'safeUrl() + the URL_FIELDS priority walk', shows: 'sourceUrl' },

  /* `canonicalStatus()` — the line's own word is mapped onto the shared enum,
   * so "Open" is displayed as "To Pursue". */
  sr_rfp_status: { via: 'canonicalStatus()', shows: 'pursuit' },

  /* `parseDate()` → `effDate`, which every dated view and the countdown key
   * off. */
  closedate: { via: 'parseDate()', shows: 'effDate' },

  /* ⚠️ THE ONE MOST WORTH KNOWING ABOUT, because it is the owner field and the
   * owner backfill is the point of this build. `adaptPursuitRow` resolves the
   * displayed owner as
   *     pursuitDeal.owner || partnershipDeal.owner || sr_assigned_rep || …
   * so on a record carrying an associated deal WITH an owner, writing
   * `sr_assigned_rep` lands correctly on the record and the board's Owner
   * column does not change — the deal's owner still wins. Exactly the
   * `sr_event_date`/`effDate` shape, where confirmed still wins. */
  sr_assigned_rep: { via: 'the owner fallback chain (an associated deal\'s owner wins)', shows: 'rep' },

  /* Feeds the composite `hqLocation` ("Stamford, Connecticut") as well as
   * `city`, so patching one without the other would show a composite the
   * record does not hold. */
  city: { via: 'the composite hqLocation', shows: 'hqLocation' },
}

export function isPursuitDerivedField(field) {
  return Object.prototype.hasOwnProperty.call(ADAPTER_DERIVED, S(field))
}

/** The human note for a derived field, or null. Shown beside the "now" value so
 *  the operator is told why the row above may read differently — instead of
 *  being left to wonder, which is how somebody "fixes" a good value. */
export function pursuitDerivedNote(field) {
  const d = ADAPTER_DERIVED[S(field)]
  if (!d) return null
  return { via: d.via, shows: d.shows }
}

/* ---- THE CURRENT VALUE, AS THE RECORD ACTUALLY HOLDS IT -----------------
 *
 * ⚠️ THE RAW HUBSPOT PROPERTY, ALWAYS — never the normalized value. See
 * ADAPTER_DERIVED above for what happens otherwise.
 *
 * ⚠️ AND THE FALLBACK IS PER-RECORD, NOT PER-KEY. If the record carries a `raw`
 * object at all, that object IS the truth and a missing key means the property
 * is EMPTY. Falling back per missing key looks equivalent and is not: the
 * adapter INVENTS values that no property holds (`dateStatus` becomes 'N/A',
 * `pursuit` becomes 'Not Started' on a blank status), so a per-key fallback
 * would show the operator a word the record has never held and then refuse
 * their edit to it as a no-change. The normalized shape is the fallback only
 * for a record with no `raw` whatsoever — `?demo=1` and test fixtures.
 */
export function currentPursuitValue(record, field) {
  const c = record || {}
  const f = S(field)
  const hasRaw = c.raw && typeof c.raw === 'object'
  const v = hasRaw ? c.raw[f] : undefined
  if (v == null) return ''
  return String(v)
}

/** What the record will read afterwards, in the operator's own terms — an enum
 *  renders by its LABEL, because "ATTEMPTED_TO_CONTACT" is the wire value and
 *  "Attempted to Contact" is what a person recognises. */
export function renderPursuitValue(policy, field, value) {
  const spec = specOf(policy, field)
  const s = S(value)
  if (!s) return '(blank)'
  if (spec?.kind === 'enum') {
    const hit = spec.options.find((o) => o.value === s)
    return hit ? hit.label : s
  }
  return s
}

/* ---- IS THIS EDIT EVEN A CHANGE, AND IS IT SAYABLE ---------------------- */
export function pursuitEditProblem({ policy, field, current, next, reason, gate, clearOk = false }) {
  const spec = specOf(policy, field)
  if (!spec) return { kind: 'unknown-field', message: `${field} is not a correctable field on this line.` }
  if (!gate?.editable) return { kind: 'denied', message: gate?.why || 'Not permitted.' }

  const a = S(current)
  const b = S(next)

  /* ⚠️ A NO-CHANGE WRITE IS REFUSED HERE RATHER THAN SENT. The route accepts it
   * and answers `ok:true` with the field in `wrote` — a real ledger row for a
   * value nobody changed, polluting the one trail somebody will read when they
   * need to know who changed an owner and why. */
  if (a === b) {
    return { kind: 'nochange', message: 'That is what the record already says. Nothing to correct.' }
  }

  /* ⚠️ BLANKING IS A REAL CORRECTION and not a mistake to prevent — clearing a
   * wrong industry or a dead submission URL is the right answer. But the route
   * drops an undeclared blank into `ignored` and answers ok having changed
   * nothing, so it is declared explicitly rather than sent naively. */
  if (!b && !clearOk) {
    return {
      kind: 'blanking',
      declare: true,
      message: 'Clearing a field is a real correction, and the route drops an undeclared blank '
        + 'without writing it — so it has to be said explicitly.',
    }
  }

  /* ⚠️ AN ENUM VALUE OFF THE LIST IS THE `sr_segment` FAILURE. The route accepts
   * it and HubSpot rejects it afterwards — 68 PSAL records went exactly that
   * way. The select makes this unreachable by hand; the check is here because a
   * restored draft or a seeded value can still carry one. */
  if (b && spec.kind === 'enum' && !spec.options.some((o) => o.value === b)) {
    return {
      kind: 'badenum',
      message: `${spec.label} is an enum and "${b}" is not one of the ${spec.options.length} values the `
        + `portal holds. HubSpot rejects an off-list value AFTER the route has accepted the call.`,
    }
  }

  if (b && spec.kind === 'number' && !/^-?\d+(\.\d+)?$/.test(b)) {
    return { kind: 'badnumber', message: `${spec.label} is a number. Digits only — no commas.` }
  }

  if (b && spec.kind === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(b)) {
    return { kind: 'baddate', message: 'Use YYYY-MM-DD.' }
  }

  const why = S(reason)
  if (!why) {
    return {
      kind: 'noreason',
      message: 'A reason is required. It goes to the ledger, and it is the only thing that will '
        + 'explain this change to whoever reads the record next.',
    }
  }
  if (why.length < REASON_MIN) {
    return {
      kind: 'thinreason',
      message: `That reason is ${why.length} characters. Say what was wrong and how this value was `
        + `established — at least ${REASON_MIN} characters.`,
    }
  }

  return null
}

/* ---- THE REQUEST -------------------------------------------------------
 *
 * ⚠️ ONE FIELD PER CALL, DELIBERATELY. The route takes several at once, and a
 * multi-field write is where a refusal becomes unreadable: `data_repair_locked`
 * names the fields it refused, so a three-field write that half-lands leaves
 * the operator reading a partial success against three before/after pairs. One
 * field per call means every verdict is about the thing on screen.
 */
export function buildPursuitEditRequest({ line, id, field, value, reason, gate, clearing = false }) {
  const body = {
    line: S(line),
    id: String(id),
    fields: { [S(field)]: S(value) },
    reason: S(reason),
  }
  /* Sent for a privileged field because the route's own refusal names it
   * ("requires Data Repair … plus `override: true` and a reason"). NOT sent for
   * an operational one — an override flag on a write that needs no override is
   * a habit that makes the flag meaningless. */
  if (gate?.tier === 'privileged') body.override = true
  if (clearing) body.allowClear = [S(field)]
  return body
}

/* ---- READING THE VERDICT ----------------------------------------------
 *
 * ⚠️ `wrote` IS THE ONLY KEY THAT MEANS A CHANGE HAPPENED, and unlike
 * `champion-update` it is present in BOTH directions — the backend took the
 * plan's recommendation not to reproduce that wart. Measured live 2026-09-14
 * against the real route:
 *
 *  200 { ok:true,  wrote:['city'], fields:['city'], ledger:[{field,seq,at}], tier, by, confirm }
 *  400 { ok:false, error:'no_updatable_fields', wrote:[], refused:[…], rejected, ignored, allowed, tier }
 *  403 { ok:false, error:'data_repair_locked',  wrote:[], refused:[…], fields:[…], logged:{seq,at} }
 *  403 { ok:false, error:'field_forbidden',     wrote:[], refused:[…], never:[…], logged:{seq,at} }
 *
 * ⚠️ NOTE `fields` ECHOES THE REFUSED FIELD ON A 403. It is NOT a written list —
 * reading it would report every refusal as a success, which is the single
 * failure mode this function exists to prevent. `wrote` is read, `fields` is
 * not, and `ok === true` alone is not enough either: the route has answered 200
 * having changed nothing when a blank landed in `ignored`.
 */
export function readPursuitEditResult(res, field) {
  const r = res || {}
  const f = S(field)
  const wrote = arr(r.wrote).map(S)

  /* THE ONLY YES. Both halves required. */
  if (r.ok === true && wrote.includes(f)) {
    const seq = arr(r.ledger).find((l) => S(l?.field) === f)?.seq
    return {
      kind: 'written',
      field: f,
      tier: S(r.tier),
      by: S(r.by),
      seq: seq ?? null,
      message: seq
        ? `The correction is written, and the reason is in the ledger (seq ${seq}).`
        : 'The correction is written, and the reason is in the ledger.',
    }
  }

  /* A 200 that did not name the field in `wrote`. The route accepted the call
   * and changed nothing — an ignored blank, or a field it silently dropped.
   * Reported as the no-op it is, never as a success. */
  if (r.ok === true) {
    return {
      kind: 'nothing-written',
      message: wrote.length
        ? `The server reports it wrote ${wrote.join(', ')} — not ${f}.`
        : 'The server accepted the call and named no field it wrote.',
      hint: 'Nothing was changed on this field. Reload the record before trying again.',
    }
  }

  const err = S(r.error)
  const refused = arr(r.refused).map(S)
  const seq = r.logged?.seq

  if (err === 'field_forbidden') {
    return {
      kind: 'refused',
      message: S(r.reason) || `${f} can never be written through this route.`,
      hint: seq ? `The refusal is in the ledger (seq ${seq}). Nothing was written.` : 'Nothing was written.',
    }
  }

  if (err === 'data_repair_locked') {
    return {
      kind: 'denied',
      message: S(r.reason) || 'These fields are the record\'s identity and are locked without the Data Repair grant.',
      hint: seq
        ? `The refusal is in the ledger (seq ${seq}). Nothing was written, and this is an exec's grant `
          + `to set — not something to route around.`
        : 'Nothing was written.',
    }
  }

  if (err === 'no_updatable_fields') {
    return {
      kind: 'refused',
      message: refused.includes(f)
        ? `The server does not accept ${f} at the ${S(r.tier) || 'current'} tier.`
        : S(r.reason) || 'Nothing was written.',
      hint: 'Nothing was written.',
    }
  }

  if (err === 'unknown_line') {
    return {
      kind: 'refused',
      message: S(r.reason) || `${S(r.line)} is not one of this portal's HubSpot lines.`,
      hint: 'Nothing was written.',
    }
  }

  return {
    kind: 'refused',
    message: S(r.reason) || err || 'The correction did not run.',
    hint: 'Nothing was written.',
  }
}

/* ---- THE RE-READ -------------------------------------------------------
 *
 * ⚠️ A CONFIRMED WRITE IS STILL NOT PROOF THE RECORD READS THE NEW VALUE.
 * `sr_segment` is an enum HubSpot rejects AFTER the route has accepted the
 * call — the way 68 PSAL records were lost. So the record is read back and the
 * value compared, and a mismatch is its own outcome rather than being folded
 * into the success.
 *
 * ⚠️ AND THE RE-READ MUST NOT BE THE BOARD'S PAGED LIST. `GET /api/pursuits` is
 * cursor-paged; confirming against it would be timing noise dressed as proof.
 * `GET /api/pursuit/:line/:id` is the direct read — the route's own response
 * says so in a `note`, and it returns RAW unprojected properties, which is what
 * is compared. Putting the adapter between the write and its confirmation would
 * mean comparing a transformed value against an untransformed one.
 */
export function confirmPursuitWrite({ policy, field, expected, afterRaw }) {
  const f = S(field)

  if (!afterRaw) {
    return {
      state: 'unverified',
      message: 'The server confirmed the write, but the record could not be read back — so what it '
        + 'reads now is not known here.',
      hint: 'Reopen the record to see its current state.',
    }
  }

  const raw = afterRaw[f]
  const live = raw == null ? '' : String(raw)
  const spec = specOf(policy, f)

  /* A date property comes back as a full ISO stamp; the input is YYYY-MM-DD. */
  const norm = (v) => (spec?.kind === 'date' ? S(v).slice(0, 10) : S(v))

  if (norm(live) === norm(expected)) {
    return {
      state: 'confirmed',
      live,
      message: `The record now reads ${renderPursuitValue(policy, f, live)}.`,
    }
  }

  return {
    state: 'mismatch',
    live,
    message: '⚠️ The server confirmed the write, but reading the record back gives '
      + `${renderPursuitValue(policy, f, live)} — not ${renderPursuitValue(policy, f, expected)}. `
      + (spec?.kind === 'enum'
        ? 'This field is an enum: HubSpot rejects a value that is not on its list, and it does so '
          + 'after the route has already accepted the call.'
        : 'The value was transformed on the way in, or something changed it back.'),
    /* ⚠️ DRAWN AS **NOT CORRECTED**, in the danger tone. Every other zero-write
     * outcome left the record alone; this one did not, and reporting it as a
     * success is how a bad value survives a correction that looked like it
     * worked. */
    hint: 'Treat this field as NOT corrected.',
  }
}

/* ---- WHO CAN BE ASKED --------------------------------------------------
 *
 * The mirror of `canEditFields` in `fieldEdit.js`, pointed the other way: that
 * one requires `!record.sourceType` (a championship), this one requires it (a
 * pursuit row), so the two can never both draw on the same record.
 *
 * `sourceId` is the RAW un-namespaced HubSpot id. `record.id` is prefixed with
 * the space key (`corporate:55774529893`) so it cannot collide with a
 * championship in a selection or an export, and composing the real id by
 * stripping that prefix would be fragile — the adapter already carries it.
 */
export function canEditPursuitFields(record, category) {
  const c = record || {}
  if (!c.sourceType) return false
  if (!S(c.sourceId)) return false
  /* The space must declare it. Widened per-space and deliberately, never
   * globally — see `categories.js#allowsFieldEdits`. */
  return category?.allowsFieldEdits === true
}

/** The audit `before` pair. The old value is the single thing most worth having
 *  in the trail — after the write nothing on the record holds it. */
export function pursuitEditBefore(record, field) {
  return {
    field: S(field),
    was: currentPursuitValue(record, field),
    record: S(record?.name),
    line: S(record?.lineKey),
  }
}

/* ---- THE OPTIMISTIC PATCH ----------------------------------------------
 *
 * In NORMALIZED keys, composed only from the field the server NAMED in `wrote`
 * and the re-read confirmed — and **null for anything the adapter transforms**.
 *
 * Re-implementing `normalizeState`, `safeUrl`, `canonicalStatus` or the owner
 * fallback chain here would be a second copy of the adapter, and the copy would
 * drift. A row that is briefly stale is recoverable; a row showing a value the
 * record does not hold is the two-sources-of-truth fault this board has been
 * bitten by before (`onHunted` in CommandCenter.jsx patches raw HubSpot keys
 * into a store of normalized records and has never actually repainted the
 * column it targets). Returns null rather than guessing.
 */
const PATCH_KEY = {
  name: 'name',
  dealname: 'name',
  industry: 'industry',
  hs_lead_status: 'pursuitLevel',
  sr_org_type: 'orgType',
  country: null,
  domain: null,
}

export function pursuitEditPatch(field, value) {
  const f = S(field)
  if (isPursuitDerivedField(f)) return null
  if (!Object.prototype.hasOwnProperty.call(PATCH_KEY, f)) return null
  const key = PATCH_KEY[f]
  if (!key) return null
  return { [key]: S(value) }
}
